import ExcelJS from "exceljs";

// src/utils/totalGuestsPerDayCsvUtils.js

/**
 * Convert an array of objects into a CSV string.
 * Uses the keys of the first object as headers.
 * @param {Object[]} objArray
 * @returns {string}
 */
export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  let csv = headers.map(escape).join(",") + "\r\n";

  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

/**
 * Export a CSV of total guests per day of week by transaction count.
 * Categories are determined by tx.cust_count:
 *  - 1 => Solo
 *  - 2 => Couple
 *  - 3-4 => Small Group
 *  - 5-7 => Medium Group
 *  - >=8 => Large Group
 *
 * @param {Array}  transactions  - filtered list of transactions with tran_date and cust_count
 * @param {string} storeName     - name of the store (e.g., 'All Stores')
 * @param {string} [fileName]    - CSV download filename
 */
// export function exportTotalGuestsPerDayCSV(
//   transactions,
//   storeName,
//   fileName = "TotalGuestsPerDay.csv"
// ) {
//   const days = [
//     "Sunday",
//     "Monday",
//     "Tuesday",
//     "Wednesday",
//     "Thursday",
//     "Friday",
//     "Saturday",
//   ];
//   const categories = [
//     "Solo",
//     "Couple",
//     "Small Group",
//     "Medium Group",
//     "Large Group",
//   ];

//   // Initialize counts
//   const dataByDay = {};
//   days.forEach((day) => {
//     dataByDay[day] = categories.reduce(
//       (acc, cat) => ({ ...acc, [cat]: 0 }),
//       {}
//     );
//   });

//   // Tally transactions
//   transactions.forEach((tx) => {
//     const day = new Date(tx.tran_date).toLocaleString("default", {
//       weekday: "long",
//     });
//     const count = parseInt(tx.cust_count, 10);
//     if (!dataByDay[day]) return;

//     if (count === 1) dataByDay[day].Solo++;
//     else if (count === 2) dataByDay[day].Couple++;
//     else if (count >= 3 && count <= 4) dataByDay[day]["Small Group"]++;
//     else if (count >= 5 && count <= 7) dataByDay[day]["Medium Group"]++;
//     else if (count >= 8) dataByDay[day]["Large Group"]++;
//   });

//   // Build rows
//   const rows = days.map((day) => ({
//     Day: day,
//     Store: storeName,
//     ...dataByDay[day],
//   }));

//   // Convert and download
//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportTotalGuestsPerDayCSV: no data to export");
//     return;
//   }

//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportTotalGuestsPerDayCSV(
  transactions,
  storeName,
  fileName = "TotalGuestsPerDay.xlsx",
) {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const categories = [
    "Solo",
    "Couple",
    "Small Group",
    "Medium Group",
    "Large Group",
  ];

  // Initialize counts
  const dataByDay = {};
  days.forEach((day) => {
    dataByDay[day] = categories.reduce((acc, cat) => {
      acc[cat] = 0;
      return acc;
    }, {});
  });

  // Tally transactions
  (transactions || []).forEach((tx) => {
    const day = new Date(tx.tran_date).toLocaleString("default", {
      weekday: "long",
    });
    const count = Number.parseInt(tx.cust_count, 10);

    if (!dataByDay[day] || Number.isNaN(count)) return;

    if (count === 1) dataByDay[day].Solo++;
    else if (count === 2) dataByDay[day].Couple++;
    else if (count >= 3 && count <= 4) dataByDay[day]["Small Group"]++;
    else if (count >= 5 && count <= 7) dataByDay[day]["Medium Group"]++;
    else if (count >= 8) dataByDay[day]["Large Group"]++;
  });

  // Build rows (+ Total per day)
  const rows = days.map((day) => {
    const r = { Day: day, Store: storeName || "All Stores", ...dataByDay[day] };
    r.Total =
      r.Solo +
      r.Couple +
      r["Small Group"] +
      r["Medium Group"] +
      r["Large Group"];
    return r;
  });

  // Totals for bottom row
  const totals = rows.reduce(
    (acc, r) => {
      acc.Solo += r.Solo;
      acc.Couple += r.Couple;
      acc.Small += r["Small Group"];
      acc.Medium += r["Medium Group"];
      acc.Large += r["Large Group"];
      acc.Total += r.Total;
      return acc;
    },
    { Solo: 0, Couple: 0, Small: 0, Medium: 0, Large: 0, Total: 0 },
  );

  // ===== Workbook =====
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Guests by Day", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // ===== Title =====
  sheet.mergeCells("A1:H1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Total Guest vs. Day of Week";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // ===== Meta =====
  sheet.mergeCells("A2:H2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: Angels Pizza   |   Store: ${storeName || "All Stores"}   |   Grouping: Solo / Couple / Group sizes`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  // Spacer
  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // ===== Header =====
  const headers = [
    "Day",
    "Store",
    "Solo (1)",
    "Couple (2)",
    "Small Group (3-5)",
    "Medium Group (6-10)",
    "Large Group (11-15)",
    "Total",
  ];
  sheet.addRow(headers);

  // Data rows
  rows.forEach((r) => {
    sheet.addRow([
      r.Day,
      r.Store,
      r.Solo,
      r.Couple,
      r["Small Group"],
      r["Medium Group"],
      r["Large Group"],
      r.Total,
    ]);
  });

  // Column widths
  sheet.columns = [
    { width: 14 },
    { width: 18 },
    { width: 12 },
    { width: 12 },
    { width: 18 },
    { width: 20 },
    { width: 18 },
    { width: 10 },
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Header styling
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Body styling + striped rows
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, col) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      if (col <= 2) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.numFmt = "#,##0";
      }

      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // ===== Totals row =====
  const totalRowIndex = lastDataRow + 1;
  sheet.getRow(totalRowIndex).height = 18;

  sheet.mergeCells(`A${totalRowIndex}:B${totalRowIndex}`);
  const totalLabelCell = sheet.getCell(`A${totalRowIndex}`);
  totalLabelCell.value = "TOTAL";
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { vertical: "middle", horizontal: "left" };
  totalLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  const totalsArr = [
    totals.Solo,
    totals.Couple,
    totals.Small,
    totals.Medium,
    totals.Large,
    totals.Total,
  ];

  // C..H (6 columns)
  for (let i = 0; i < totalsArr.length; i++) {
    const cell = sheet.getCell(totalRowIndex, 3 + i); // 3 = column C
    cell.value = totalsArr[i];
    cell.font = { bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.numFmt = "#,##0";
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFF6FF" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FF93C5FD" } },
      left: { style: "thin", color: { argb: "FF93C5FD" } },
      bottom: { style: "thin", color: { argb: "FF93C5FD" } },
      right: { style: "thin", color: { argb: "FF93C5FD" } },
    };
  }

  // Auto-filter
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Download (no file-saver) =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
